<script setup lang="ts">
/**
 * UI-kit component
 * Category: Other
 * Name: DropZoneItem
 */
import type { DropZoneItemProps } from '../types/interfaces'

import { faFile, faArrowUpRightFromSquare, faArrowDownToLine, faTrashCan, faArrowRotateRight } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { AppIcon } from '@shared/ui/icons'
import { BtnIcon } from '@shared/ui/buttons'

import { useDropZoneItem } from '../composables/use-drop-zone-item.hook'

defineOptions({
  name: 'DropZoneItem',
})

const props = withDefaults(defineProps<DropZoneItemProps>(), {
  progress: 0,
  viewLink: null,
  downloadLink: null,
  disabled: false,
  error: null,
  removable: true,
})

const emit = defineEmits<{
  remove: []
  retry: []
}>()

const {
  formattedSize,
  showProgress,
  progressValue,
  hasViewLink,
  hasDownloadLink,
  isError,
  openView,
  openDownload,
} = useDropZoneItem({ props })

const handleRemove = (): void => emit('remove')
const handleRetry = (): void => emit('retry')
</script>

<template>
  <div class="drop-zone-item">
    <div class="drop-zone-item__main">
      <AppIcon
        :icon="faFile"
        size="small"
        color="slate-500"
        class="drop-zone-item__icon"
      />
      <AppText
        name="body2"
        color="slate-900"
        truncate
        class="drop-zone-item__name"
      >
        {{ name }}
      </AppText>
      <AppText
        v-if="isError && error"
        name="body2"
        color="red-600"
        truncate
        class="drop-zone-item__error"
      >
        {{ error }}
      </AppText>
      <AppText
        v-else
        name="body2-medium"
        color="slate-400"
        class="drop-zone-item__size"
      >
        {{ formattedSize }}
      </AppText>
    </div>

    <div class="drop-zone-item__actions">
      <BtnIcon
        v-if="isError"
        :icon="faArrowRotateRight"
        color="black"
        size="medium"
        title="Retry upload"
        :disabled="disabled"
        @click="handleRetry"
      />
      <BtnIcon
        v-if="hasViewLink"
        :icon="faArrowUpRightFromSquare"
        color="black"
        size="medium"
        title="Open in new tab"
        :disabled="disabled"
        @click="openView"
      />
      <BtnIcon
        v-if="hasDownloadLink"
        :icon="faArrowDownToLine"
        color="blue"
        size="medium"
        title="Download"
        :disabled="disabled"
        @click="openDownload"
      />
      <BtnIcon
        v-if="removable"
        :icon="faTrashCan"
        color="red"
        size="medium"
        title="Delete"
        :disabled="disabled"
        @click="handleRemove"
      />
    </div>

    <div
      v-if="showProgress"
      class="drop-zone-item__progress"
    >
      <div
        class="drop-zone-item__progress-bar"
        :style="{ width: `${progressValue}%` }"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped src="../styles/_drop-zone-item.scss" />
